// src/pricingtable/ReviewChangesDrawer2.jsx
/* ------------------------------------------------------------------
 * src/pricingtable/ReviewChangesDrawer2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * Parallel review/confirmation drawer for the new WordPress-aligned
 * SubscriptionPage2 pricing flow.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * We are intentionally migrating the subscription/pricing UI in parallel:
 *   • SubscriptionPage2.jsx
 *   • PricingPageHeader2.jsx
 *   • PricingTable2.jsx
 *   • PlanCard2.jsx
 *   • PricingCompareTable2.jsx
 *   • PricingFaq2.jsx
 *
 * To preserve the current production billing flow, we do NOT modify the
 * existing ReviewChangesDrawer.jsx yet.
 *
 * This drawer:
 *   • keeps the same billing/runtime integrations from BillingContext
 *   • aligns its copy/visual structure with the new pricing page
 *   • assumes selectedPlan comes from the new pricing payload-driven UI
 *
 * WHAT THIS FILE REUSES
 * ------------------------------------------------------------------
 * The runtime billing logic remains app-owned and is still sourced from:
 *   • previewBillingChange()
 *   • switchIntervalAndPay()
 *   • currentSubscription state
 *   • hasValidPaymentMethod state
 *   • restart / downgrade scheduling behavior
 *
 * OWNERSHIP BOUNDARY
 * ------------------------------------------------------------------
 * ReviewChangesDrawer2 is the active owner of the review + confirm path for
 * the new SubscriptionPage2 flow.
 *
 * That means this file owns:
 *   • preview loading
 *   • restart / downgrade messaging
 *   • final checkout / billing handoff
 *
 * SubscriptionPage2 owns only page-level selection state:
 *   • selectedPlan
 *   • pendingPlan
 *   • seatCount
 *   • pricingInterval
 *   • drawer open / close
 *
 * WHAT THIS FILE UPDATES
 * ------------------------------------------------------------------
 * 1. Cleaner alignment with the new plan objects emitted by:
 *      • PlanCard2
 *      • PricingCompareTable2
 * 2. Updated copy hierarchy
 * 3. Updated cost summary language
 * 4. Safer logs for local validation
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * This file remains a REVIEW / CONFIRMATION step.
 * It is NOT the source of truth for billing math.
 * Stripe / preview logic still comes from the backend via BillingContext.
 * ------------------------------------------------------------------*/

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  Tooltip,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CircularProgress from "@mui/material/CircularProgress";

import { useBillings } from "../contexts/BillingContext";
import { planConfigs } from "plan-catalog";

/* ------------------------------------------------------------------
 * Build quick map: priceId -> month | year
 * ------------------------------------------------------------------ */
const priceIdToInterval = (() => {
  const normalizeIntervalLabel = (label) => {
    if (label === "monthly") return "month";
    if (label === "annual") return "year";
    return label;
  };

  const map = {};

  Object.values(planConfigs).forEach((plan) => {
    Object.entries(plan.pricing || {}).forEach(([intervalKey, tier]) => {
      if (tier && tier.priceId) {
        map[tier.priceId] = normalizeIntervalLabel(intervalKey);
      }
    });
  });

  return map;
})();

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */
function formatCurrency(value) {
  const numeric = Number(value || 0);
  const abs = Math.abs(numeric);

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(abs);

  return numeric < 0 ? `-${formatted}` : formatted;
}

function formatCurrencyNoCents(value) {
  const numeric = Number(value || 0);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatPlanChipLabel(label) {
  return String(label || "").trim() || "Plan";
}

function getPlanIntervalFromSelection(selectedPlan, fallbackInterval) {
  const explicit = selectedPlan?.interval || null;
  if (explicit === "monthly") return "month";
  if (explicit === "annual") return "year";

  const priceId = selectedPlan?.priceId || null;
  if (priceId && priceIdToInterval[priceId]) {
    return priceIdToInterval[priceId];
  }

  return fallbackInterval || null;
}

/* ------------------------------------------------------------------
 * Short-lived authoritative preview cache
 *
 * Why:
 * - The drawer can revisit the exact same review state multiple times
 *   during a short interaction window:
 *     • drawer close/reopen
 *     • bfcache return
 *     • state churn while pending invoice reconciliation settles
 * - We already have a strong identity key in previewRequestKey.
 * - Reusing a recent backend-authoritative preview result for that same
 *   key reduces latency without replacing backend truth.
 *
 * Enterprise rule:
 * - Cache only successful preview reads.
 * - Keep the cache very short-lived.
 * - Never use this to bypass mutation or settlement logic.
 * ------------------------------------------------------------------ */
const PREVIEW_CACHE_TTL_MS = 30 * 1000;

/* ------------------------------------------------------------------
 * Preview response helpers
 * ------------------------------------------------------------------ */
function isPendingBillingChangePreviewUnavailable(data) {
  return (
    data?.previewUnavailable === true &&
    data?.reason === "PENDING_BILLING_CHANGE"
  );
}

/* ------------------------------------------------------------------
 * Stripe payment-page helpers
 *
 * Why:
 * - ReviewChangesDrawer2 owns final navigation UX.
 * - The backend should be the primary source of truth for what kind of
 *   Stripe page is being returned.
 * - We still keep a small URL-based fallback so the UI remains resilient
 *   during staged rollout if an older function response is returned.
 *
 * Enterprise rule:
 * - checkout        → same-tab
 * - hosted_invoice  → edge/recovery/manual-pay flow
 * - none            → no external Stripe page
 * ------------------------------------------------------------------ */
function detectStripePaymentPageTypeFromUrl(url) {
  if (typeof url !== "string" || !url.trim()) return "none";

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();

    if (
      host === "checkout.stripe.com" ||
      path.includes("/c/pay")
    ) {
      return "checkout";
    }

    if (
      host === "invoice.stripe.com" ||
      path.includes("/i/acct_")
    ) {
      return "hosted_invoice";
    }

    return "none";
  } catch (_) {
    return "none";
  }
}

function resolveStripePaymentPageType(resp) {
  const explicitType =
    (resp?.paymentPageType || "").toString().trim().toLowerCase();

  if (
    explicitType === "checkout" ||
    explicitType === "hosted_invoice" ||
    explicitType === "none"
  ) {
    return explicitType;
  }

  return detectStripePaymentPageTypeFromUrl(resp?.url || "");
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
const ReviewChangesDrawer2 = ({
  open,
  onClose,
  onPendingBillingCancelSuccess,
  reviewContext,
  onStripeHandoffStart,
}) => {
  /* ---------------------------------------------------------------
   * Billing context
   * ------------------------------------------------------------- */
const {
  currentSubscription,
  pendingChangeState,
  previewBillingChange,
  switchIntervalAndPay,
  cancelPendingBillingChange,
  hasValidPaymentMethod,
} = useBillings();


  /* ---------------------------------------------------------------
   * UI state
   * ------------------------------------------------------------- */
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelingPendingChange, setCancelingPendingChange] =
    useState(false);
  const [requiresRestart, setRequiresRestart] = useState(false);
  const [restartReason, setRestartReason] = useState(null);

  const [resumePaymentMode, setResumePaymentMode] = useState(false);
  const [resumeInvoiceUrl, setResumeInvoiceUrl] = useState(null);
  const [resumeInvoiceId, setResumeInvoiceId] = useState(null);
  const [resumeInvoiceStatus, setResumeInvoiceStatus] = useState(null);
  const [resumePaymentStatus, setResumePaymentStatus] = useState(null);

const resetTransientUiState = useCallback(() => {
  console.log(
    "[ReviewChangesDrawer2] Resetting transient drawer UI state.",
  );

  setSubmitting(false);
  setLoading(false);
  setCancelingPendingChange(false);
}, []);

  /* ---------------------------------------------------------------
   * Preview request guards
   *
   * Why:
   * - The drawer can be closed or re-opened while an async billing
   *   preview is still in flight.
   * - We only want the MOST RECENT preview request to be allowed
   *   to write UI state.
   * - We also want to avoid duplicate requests for the exact same
   *   review state while one is already in flight.
   * - A request key should only count as "completed" after a
   *   non-stale success path has actually finished.
   * ------------------------------------------------------------- */
  const previewRequestSeqRef = useRef(0);
  const activePreviewRequestRef = useRef(0);
  const inFlightPreviewRequestKeyRef = useRef(null);
  const lastCompletedPreviewRequestKeyRef = useRef(null);

  /* ---------------------------------------------------------------
   * Short-lived preview result cache
   *
   * Why:
   * - Reusing a recent authoritative preview result for the same
   *   previewRequestKey reduces avoidable drawer latency.
   * - The key already encodes the review state identity we care about.
   * ------------------------------------------------------------- */
  const previewCacheRef = useRef(new Map());

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const drawerWidth = fullScreen ? "100%" : 460;

  /* ---------------------------------------------------------------
   * Early derived values
   * ------------------------------------------------------------- */
  const stableSelectedPlan =
    reviewContext?.selectedPlan || null;

  const stableSeatCount =
    Number(reviewContext?.seatCount || 0);

  const stableCurrentSubscription =
    reviewContext?.currentSubscription || currentSubscription || null;

  const selectedKey =
    (stableSelectedPlan?.key || stableSelectedPlan?.name || "")
      .toString()
      .toLowerCase();

  const selectedCfg = planConfigs[selectedKey] || {};
  const selectedSelfServe =
    (selectedCfg?.availability?.selfServe !== false) &&
    (selectedCfg?.cta?.type !== "contact_sales") &&
    (selectedCfg?.pricingDisplay !== "contact");

  const currentPlanKey =
    (
      stableCurrentSubscription?.planName ||
      stableCurrentSubscription?.planKey ||
      ""
    )
      .toString()
      .toLowerCase();

  const currentCfg = planConfigs[currentPlanKey] || {};
  const currentPlanIsSalesLed =
    (currentCfg?.availability?.selfServe === false) ||
    (currentCfg?.cta?.type === "contact_sales") ||
    (currentCfg?.pricingDisplay === "contact");
    
  const currentSubscriptionId =
    stableCurrentSubscription?.id || null;
  const currentSubscriptionItemId =
    stableCurrentSubscription?.stripeSubscriptionItemId || null;
  const currentSubscriptionPriceId =
    stableCurrentSubscription?.priceId || null;
  const currentSubscriptionStatus =
    stableCurrentSubscription?.status || null;
  
  const currentSubscriptionInterval =
    stableCurrentSubscription?.interval || null;
  
  const currentSubscriptionSeats =
    Number(stableCurrentSubscription?.seats || 0);

  const pendingImmediateChange =
    pendingChangeState?.pendingChange || null;

  const pendingImmediateInvoiceDetails =
    pendingChangeState?.invoiceDetails || null;

  const pendingImmediateInvoiceClassification =
    pendingChangeState?.kind === "resume_payment" ?
      "actionable" :
      pendingChangeState?.kind === "checking" ?
        "checking" :
        pendingChangeState?.kind === "transitional" ?
          "transitional" :
          pendingChangeState?.kind === "stale" ?
            "stale" :
            pendingChangeState?.kind === "error" ?
              "error" :
              "idle";

  const pendingImmediateInvoiceIsActionable =
    pendingChangeState?.canResumePayment === true ||
    pendingImmediateInvoiceClassification === "actionable";

  const pendingImmediateInvoiceIsChecking =
    pendingImmediateInvoiceClassification === "checking";

  const pendingImmediateInvoiceIsTransitional =
    pendingImmediateInvoiceClassification === "transitional";

  const pendingImmediateInvoiceIsStale =
    pendingImmediateInvoiceClassification === "stale";

  const pendingImmediateInvoiceIsError =
    pendingImmediateInvoiceClassification === "error";

  const pendingImmediateInvoiceId =
    pendingChangeState?.invoiceId || null;

  const pendingImmediateNextSeatCount =
    Number.isFinite(Number(pendingChangeState?.nextSeatCount)) ?
      Number(pendingChangeState.nextSeatCount) :
      null;

  const pendingImmediateNextPriceId =
    pendingChangeState?.nextPriceId || null;

  const pendingImmediateInvoiceUrl =
    pendingChangeState?.invoiceUrl || null;

const pendingImmediateInvoiceStatus =
  pendingChangeState?.invoiceStatus ||
  pendingImmediateInvoiceDetails?.invoiceStatus ||
  pendingImmediateInvoiceDetails?.paymentStatus ||
  null;

const pendingImmediatePaymentStatus =
  pendingImmediateInvoiceDetails?.paymentIntentStatus ||
  pendingImmediateInvoiceDetails?.paymentStatus ||
  pendingChangeState?.paymentStatus ||
  null;

  const hasActiveSub =
    !!currentSubscriptionId &&
    !!currentSubscriptionItemId;

  const isSeatOnly =
    hasActiveSub &&
    !!stableSelectedPlan?.priceId &&
    !!currentSubscriptionPriceId &&
    stableSelectedPlan.priceId === currentSubscriptionPriceId;

  const oldInterval = currentSubscriptionInterval || null;
  const newInterval = getPlanIntervalFromSelection(
    stableSelectedPlan,
    oldInterval,
  );

  const intervalSwitched =
    !!oldInterval &&
    !!newInterval &&
    !isSeatOnly &&
    oldInterval !== newInterval;

   const seatsDown =
    Number.isFinite(+stableSeatCount) &&
    Number.isFinite(currentSubscriptionSeats) &&
    Number(stableSeatCount) < Number(currentSubscriptionSeats);

  const isIntervalDowngrade =
    oldInterval === "year" && newInterval === "month";

  const isLikelyDowngrade =
    isIntervalDowngrade || seatsDown;

  const isTrialing =
    currentSubscriptionStatus === "trialing";

  const hasFailedBilling =
    currentSubscriptionStatus === "past_due" ||
    currentSubscriptionStatus === "unpaid" ||
    currentSubscriptionStatus === "incomplete";

  const isRestart =
    currentSubscriptionStatus === "canceled" ||
    currentSubscriptionStatus === "incomplete_expired" ||
    requiresRestart === true;

  const effectiveIsLikelyDowngrade = isLikelyDowngrade && !isRestart;

  const pendingChangeMatchesCurrentSelection =
    Boolean(pendingImmediateInvoiceId) &&
    (
      pendingImmediateNextSeatCount === Number(stableSeatCount)
    ) &&
    (
      (pendingImmediateNextPriceId || currentSubscriptionPriceId || null) ===
      (
        stableSelectedPlan?.priceId === currentSubscriptionPriceId ?
          currentSubscriptionPriceId :
          (stableSelectedPlan?.priceId || null)
      )
    );

  /* ---------------------------------------------------------------
   * Current-review pending-invoice relevance
   *
   * Why:
   * - BillingContext now owns whether a pending invoice is actionable,
   *   transitional, stale, or errored.
   * - ReviewChangesDrawer2 should only decide whether that already-
   *   reconciled pending invoice is relevant to THIS currently opened
   *   review state.
   * - This keeps invoice actionability in BillingContext while keeping
   *   selection-specific UX ownership in the drawer.
   * ------------------------------------------------------------- */
  const pendingInvoiceRelevantToCurrentReview =
    Boolean(pendingImmediateInvoiceId) &&
    Boolean(pendingChangeMatchesCurrentSelection);

  /* ---------------------------------------------------------------
   * Submission guard for unstable pending invoice states
   *
   * Why:
   * - BillingContext is now the source of truth for whether an
   *   in-flight invoice is still:
   *     • checking
   *     • transitional
   *     • actionable
   *     • stale
   * - ReviewChangesDrawer2 must never launch a new billing action
   *   while the invoice is still checking or transitional.
   *
   * Enterprise rule:
   * - Only allow submission when the pending invoice state is stable.
   * ------------------------------------------------------------- */
  const isSubmissionBlockedByPendingInvoiceState =
    pendingInvoiceRelevantToCurrentReview &&
    (
      pendingImmediateInvoiceIsChecking ||
      pendingImmediateInvoiceIsTransitional
    );

  const previewRequestKey = useMemo(() => {
    return JSON.stringify({
      open: Boolean(open),
      selectedPlanKey: stableSelectedPlan?.key || null,
      selectedPlanPriceId: stableSelectedPlan?.priceId || null,
      seatCount: Number(stableSeatCount || 0),
      subscriptionId: currentSubscriptionId || null,
      subscriptionItemId: currentSubscriptionItemId || null,
      subscriptionPriceId: currentSubscriptionPriceId || null,
      subscriptionStatus: currentSubscriptionStatus || null,
      pendingInvoiceId: pendingImmediateInvoiceId || null,
      pendingMatches: pendingChangeMatchesCurrentSelection,
      pendingRelevant: pendingInvoiceRelevantToCurrentReview,
      downgrade: effectiveIsLikelyDowngrade,
      selectedSelfServe,
    });
  }, [
    open,
    stableSelectedPlan?.key,
    stableSelectedPlan?.priceId,
    stableSeatCount,
    currentSubscriptionId,
    currentSubscriptionItemId,
    currentSubscriptionPriceId,
    currentSubscriptionStatus,
    pendingImmediateInvoiceId,
    pendingChangeMatchesCurrentSelection,
    pendingInvoiceRelevantToCurrentReview,
    effectiveIsLikelyDowngrade,
    selectedSelfServe,
  ]);

  /* ---------------------------------------------------------------
   * Payment-method notice (non-blocking)
   *
   * Why:
   * - Latest switchIntervalAndPay() already supports no-PM flows:
   *     • hosted invoice / manual payment flows
   *     • setup checkout for trial conversion when needed
   * - So ReviewChangesDrawer2 must NOT block submission purely because
   *   no saved payment method exists.
   *
   * Enterprise rule:
   * - Keep this as UI guidance only.
   * - Let the backend decide whether Stripe returns:
   *     • checkout
   *     • hosted invoice
   *     • setup flow
   * ------------------------------------------------------------- */
  const showPaymentMethodNotice =
    hasValidPaymentMethod === false &&
    currentSubscriptionStatus !== "trialing";
    
  const selectedPlanTotal = useMemo(() => {
    const isCustomPricingPlan =
      stableSelectedPlan?.pricingDisplay === "contact";

    if (isCustomPricingPlan) {
      return null;
    }

    const flatFee = Number(stableSelectedPlan?.flatFee || 0);
    const perSeat = Number(stableSelectedPlan?.perSeat);

    if (!Number.isFinite(perSeat)) return null;
    if (!Number.isFinite(flatFee)) return null;

    return flatFee + (perSeat * Number(stableSeatCount || 0));
  }, [
    stableSelectedPlan?.pricingDisplay,
    stableSelectedPlan?.flatFee,
    stableSelectedPlan?.perSeat,
    stableSeatCount,
  ]);

  const buildStripeLaunchSnapshot = () => {
    if (!stableCurrentSubscription) {
      return null;
    }

    return {
      subscriptionId: stableCurrentSubscription.id || null,
      priceId: stableCurrentSubscription.priceId || null,
      seats: stableCurrentSubscription.seats || null,
      interval: stableCurrentSubscription.interval || null,
      status: stableCurrentSubscription.status || null,
    };
  };

  /* ---------------------------------------------------------------
   * Enterprise-grade payment window helpers
   *
   * Why:
   * - Hosted Stripe invoice pages do not provide the same app-return
   *   UX as Checkout success/cancel URLs.
   * - Opening a blank tab synchronously from the user click gives the
   *   browser a direct user-gesture event to allow the new tab.
   * - We can then populate that tab after async billing work returns
   *   the hosted invoice URL.
   *
   * IMPORTANT:
   * - These helpers do NOT change billing correctness.
   * - They only improve how payment-required redirects are launched.
   * ------------------------------------------------------------- */
  const openBlankPaymentWindow = useCallback(() => {
    try {
      /* ----------------------------------------------------------
       * IMPORTANT:
       * - Do NOT use "noopener,noreferrer" here.
       *
       * Why:
       * - Some browsers will still open the new tab, but return a null
       *   window handle when noreferrer is used.
       * - That breaks our ability to:
       *     • write a loading state
       *     • later navigate the tab to Stripe
       *     • close it if the billing flow ends up not needing payment
       *
       * Enterprise rule:
       * - Open a controllable about:blank tab first.
       * - Then navigate it after the async billing response arrives.
       * -------------------------------------------------------- */
      const paymentWindow = window.open("", "_blank");

      if (!paymentWindow) {
        console.warn(
          "[ReviewChangesDrawer2] Browser blocked blank payment window.",
        );
        return null;
      }

      try {
        paymentWindow.document.title = "Loading secure payment page...";
        paymentWindow.document.body.style.margin = "0";
        paymentWindow.document.body.style.fontFamily =
          "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";
        paymentWindow.document.body.innerHTML = `
          <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;padding:24px;text-align:center;color:#0F1A2A;">
            <div>
              <div style="font-size:16px;font-weight:600;margin-bottom:8px;">
                Loading secure payment page...
              </div>
              <div style="font-size:14px;color:#64748b;">
                Please wait while we connect you to Stripe.
              </div>
            </div>
          </div>
        `;

        /* --------------------------------------------------------
         * Best-effort opener hardening
         *
         * Why:
         * - We need the window handle first so we can control the blank tab.
         * - After that, we can safely sever the opener relationship.
         * ------------------------------------------------------ */
        paymentWindow.opener = null;
      } catch (docErr) {
        console.warn(
          "[ReviewChangesDrawer2] Unable to write loading state into " +
          "blank payment window:",
          docErr,
        );
      }

      return paymentWindow;
    } catch (err) {
      console.warn(
        "[ReviewChangesDrawer2] Failed to open blank payment window:",
        err,
      );
      return null;
    }
  }, []);

  const navigatePaymentWindowOrFallback = useCallback((paymentWindow, url) => {
    if (!url) {
      console.warn(
        "[ReviewChangesDrawer2] Cannot navigate payment window: missing URL.",
      );
      return;
    }

    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.location.href = url;
      return;
    }

    console.warn(
      "[ReviewChangesDrawer2] Falling back to same-tab payment redirect.",
      { url },
    );
    window.location.href = url;
  }, []);

  const closePaymentWindowIfUnused = useCallback((paymentWindow) => {
    try {
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.close();
      }
    } catch (err) {
      console.warn(
        "[ReviewChangesDrawer2] Failed to close unused payment window:",
        err,
      );
    }
  }, []);

  /* ---------------------------------------------------------------
   * Diagnostics
   * ------------------------------------------------------------- */
  useEffect(() => {
    console.log("[ReviewChangesDrawer2] Mounted:", {
      drawerWidth,
      fullScreen,
    });
  }, []); // mount only

  useEffect(() => {
    console.log("[ReviewChangesDrawer2] Props/state update:", {
      reviewContext,
      stableSeatCount,
      selectedKey,
      selectedSelfServe,
      currentPlanKey,
      currentPlanIsSalesLed,
      hasActiveSub,
      isSeatOnly,
      oldInterval,
      newInterval,
      intervalSwitched,
      seatsDown,
      isIntervalDowngrade,
      isLikelyDowngrade,
      isTrialing,
      hasFailedBilling,
      isRestart,
      effectiveIsLikelyDowngrade,
      pendingChangeState,
      pendingImmediateChange,
      pendingImmediateInvoiceId,
      pendingImmediateInvoiceDetails,
      pendingImmediateInvoiceClassification,
      pendingImmediateInvoiceIsActionable,
      pendingImmediateInvoiceIsChecking,
      pendingImmediateInvoiceIsTransitional,
      pendingImmediateInvoiceIsStale,
      pendingImmediateInvoiceIsError,
      pendingChangeMatchesCurrentSelection,
      pendingInvoiceRelevantToCurrentReview,
      resumePaymentMode,
      resumeInvoiceUrl,
      resumeInvoiceStatus,
      resumePaymentStatus,
      cancelingPendingChange,
      showPaymentMethodNotice,
      stableCurrentSubscription,
      selectedPlanTotal,
    });
  }, [
    open,
    reviewContext,
    stableSeatCount,
    selectedKey,
    selectedSelfServe,
    currentPlanKey,
    currentPlanIsSalesLed,
    hasActiveSub,
    isSeatOnly,
    oldInterval,
    newInterval,
    intervalSwitched,
    seatsDown,
    isIntervalDowngrade,
    isLikelyDowngrade,
    isTrialing,
    hasFailedBilling,
    isRestart,
    effectiveIsLikelyDowngrade,
    pendingChangeState,
    pendingImmediateChange,
    pendingImmediateInvoiceId,
    pendingImmediateInvoiceDetails,
    pendingImmediateInvoiceClassification,
    pendingImmediateInvoiceIsActionable,
    pendingImmediateInvoiceIsChecking,
    pendingImmediateInvoiceIsTransitional,
    pendingImmediateInvoiceIsStale,
    pendingImmediateInvoiceIsError,
    pendingChangeMatchesCurrentSelection,
    pendingInvoiceRelevantToCurrentReview,
    resumePaymentMode,
    resumeInvoiceUrl,
    resumeInvoiceStatus,
    resumePaymentStatus,
    cancelingPendingChange,
    showPaymentMethodNotice,
    stableCurrentSubscription,
    selectedPlanTotal,
  ]);

  useEffect(() => {
    if (!resumePaymentMode) {
      return;
    }

    if (!submitting) {
      return;
    }

    console.log(
      "[ReviewChangesDrawer2] Resume-payment mode became active while " +
      "submitting was still true. Clearing stale submitting state.",
      {
        resumeInvoiceId,
        resumeInvoiceStatus,
        resumePaymentStatus,
      },
    );

    setSubmitting(false);
  }, [
    resumePaymentMode,
    submitting,
    resumeInvoiceId,
    resumeInvoiceStatus,
    resumePaymentStatus,
  ]);
  
  /* ---------------------------------------------------------------
   * Fetch proration preview
   * ------------------------------------------------------------- */
  useEffect(() => {
    let ignore = false;

    if (!open || !stableSelectedPlan) return;

    if (cancelingPendingChange) {
      console.log(
        "[ReviewChangesDrawer2] Preview fetch paused during " +
        "pending-change cancellation.",
        {
          selectedPlan: stableSelectedPlan,
          seatCount: stableSeatCount,
          previewRequestKey,
        },
      );
      return;
    }

    if (!selectedSelfServe) {
      console.warn(
        "[ReviewChangesDrawer2] blocked: selected plan is not self-serve",
        {
          selectedKey,
          selectedPlan: stableSelectedPlan,
        },
      );
      return;
    }

    /**
     * Brand-new / no active subscription:
     * compute the first-cycle estimate locally for the drawer.
     */
    const activeSubReady =
      !!currentSubscriptionId &&
      !!currentSubscriptionItemId;

    if (!activeSubReady) {
      const firstCycleTotal =
        Number(stableSelectedPlan?.flatFee || 0) +
        (Number(stableSelectedPlan?.perSeat || 0) * stableSeatCount);

      const firstCycle = {
        basePrice: Number(stableSelectedPlan?.flatFee || 0),
        additionalSeatsPrice:
          Number(stableSelectedPlan?.perSeat || 0) * stableSeatCount,
        prorationAdjustment: 0,
        total: firstCycleTotal,
        amountDueNow: firstCycleTotal,
        breakdownTotal: firstCycleTotal,
        subtotal: firstCycleTotal,
        invoiceTotal: firstCycleTotal,
        amountPaid: 0,
        amountRemaining: firstCycleTotal,
        appliedBalance: 0,
      };

      console.log("[ReviewChangesDrawer2] Local first-cycle estimate:", {
        firstCycle,
        selectedPlan: stableSelectedPlan,
        seatCount: stableSeatCount,
      });

      inFlightPreviewRequestKeyRef.current = null;
      lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

      previewCacheRef.current.set(previewRequestKey, {
        preview: firstCycle,
        requiresRestart: false,
        restartReason: null,
        cachedAt: Date.now(),
      });

      setRequiresRestart(false);
      setRestartReason(null);
      setResumePaymentMode(false);
      setResumeInvoiceUrl(null);
      setResumeInvoiceId(null);
      setResumeInvoiceStatus(null);
      setResumePaymentStatus(null);
      setPreview(firstCycle);
      setLoading(false);
      return;
    }

    const fetchPreview = async () => {
      if (inFlightPreviewRequestKeyRef.current === previewRequestKey) {
        console.log(
          "[ReviewChangesDrawer2] Skipping duplicate in-flight " +
          "preview request:",
          {
            previewRequestKey,
            selectedPlan: stableSelectedPlan,
            seatCount: stableSeatCount,
          },
        );
        return;
      }

      if (lastCompletedPreviewRequestKeyRef.current === previewRequestKey) {
        console.log(
          "[ReviewChangesDrawer2] Skipping already-completed " +
          "preview request:",
          {
            previewRequestKey,
            selectedPlan: stableSelectedPlan,
            seatCount: stableSeatCount,
          },
        );
        return;
      }

      const now = Date.now();
      const cachedPreview = previewCacheRef.current.get(previewRequestKey) || null;

      if (
        cachedPreview &&
        now - cachedPreview.cachedAt < PREVIEW_CACHE_TTL_MS
      ) {
        console.log(
          "[ReviewChangesDrawer2] Reusing cached authoritative preview:",
          {
            previewRequestKey,
            cachedAt: cachedPreview.cachedAt,
            ageMs: now - cachedPreview.cachedAt,
            selectedPlan: stableSelectedPlan,
            seatCount: stableSeatCount,
          },
        );

        inFlightPreviewRequestKeyRef.current = null;
        lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

        setRequiresRestart(Boolean(cachedPreview.requiresRestart));
        setRestartReason(cachedPreview.restartReason || null);
        setResumePaymentMode(false);
        setResumeInvoiceUrl(null);
        setResumeInvoiceId(null);
        setResumeInvoiceStatus(null);
        setResumePaymentStatus(null);
        setPreview(cachedPreview.preview || null);
        setLoading(false);

        return;
      }

      const requestId = previewRequestSeqRef.current + 1;
      previewRequestSeqRef.current = requestId;
      activePreviewRequestRef.current = requestId;
      inFlightPreviewRequestKeyRef.current = previewRequestKey;

      setLoading(true);

      if (!ignore) {
        setResumePaymentMode(false);
        setResumeInvoiceUrl(null);
        setResumeInvoiceId(null);
        setResumeInvoiceStatus(null);
        setResumePaymentStatus(null);
      }

      const isStaleRequest = () =>
        ignore || activePreviewRequestRef.current !== requestId;

      try {
        /**
         * Restart rule:
         * - canceled / incomplete_expired should force selected plan priceId
         * - otherwise, same priceId means seat-only preview
         */
        const restartFlow =
          currentSubscriptionStatus === "canceled" ||
          currentSubscriptionStatus === "incomplete_expired";

        const computedNewPriceId = restartFlow
          ? (stableSelectedPlan?.priceId || null)
          : (!stableSelectedPlan ||
             stableSelectedPlan?.priceId === currentSubscriptionPriceId
              ? null
              : stableSelectedPlan.priceId);

        /* ---------------------------------------------------------
         * Resume existing pending immediate payment
         *
         * Why:
         * - preview-first means we do not create a new in-flight mutation
         *   when one already exists for the same requested change.
         * - Instead, the drawer should pivot into a resume-payment state.
         * - Enterprise rule: only resume if the pending change belongs
         *   to this current review session.
         * ------------------------------------------------------- */
        if (
          pendingInvoiceRelevantToCurrentReview &&
          pendingImmediateInvoiceIsChecking
        ) {
          console.log(
            "[ReviewChangesDrawer2] Pending invoice is still being " +
            "reconciled by BillingContext. Waiting before deciding " +
            "resume-payment path:",
            {
              pendingImmediateChange,
              pendingImmediateInvoiceId,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = null;

          setPreview(null);
          setResumePaymentMode(false);
          setResumeInvoiceId(null);
          setResumeInvoiceUrl(null);
          setResumeInvoiceStatus(null);
          setResumePaymentStatus(null);
          setLoading(false);

          return;
        }

        if (
          pendingInvoiceRelevantToCurrentReview &&
          pendingImmediateInvoiceIsTransitional
        ) {
          console.log(
            "[ReviewChangesDrawer2] Pending invoice is transitional. " +
            "Waiting for BillingContext to publish a stable actionable " +
            "or stale state before preview continues:",
            {
              pendingImmediateChange,
              pendingImmediateInvoiceDetails,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = null;

          setPreview(null);
          setResumePaymentMode(false);
          setResumeInvoiceId(null);
          setResumeInvoiceUrl(null);
          setResumeInvoiceStatus(null);
          setResumePaymentStatus(null);
          setLoading(false);

          return;
        }

        if (
          pendingInvoiceRelevantToCurrentReview &&
          pendingImmediateInvoiceIsActionable
        ) {
          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

          setRequiresRestart(false);
          setRestartReason(null);
          setPreview(null);
          setResumePaymentMode(true);
          setResumeInvoiceId(pendingImmediateInvoiceId);
          setResumeInvoiceUrl(pendingImmediateInvoiceUrl || null);
          setResumeInvoiceStatus(pendingImmediateInvoiceStatus || null);
          setResumePaymentStatus(pendingImmediatePaymentStatus || null);

          console.log(
            "[ReviewChangesDrawer2] Resuming pending payment path " +
            "from provider-owned pendingChangeState:",
            {
              pendingChangeState,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          return;
        }

        if (
          pendingInvoiceRelevantToCurrentReview &&
          (
            pendingImmediateInvoiceIsStale ||
            pendingImmediateInvoiceIsError
          )
        ) {
          console.warn(
            "[ReviewChangesDrawer2] Pending invoice is not actionable " +
            "per BillingContext reconciliation. Clearing resume state " +
            "and continuing to fresh preview:",
            {
              pendingImmediateChange,
              pendingImmediateInvoiceDetails,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          setResumePaymentMode(false);
          setResumeInvoiceId(null);
          setResumeInvoiceUrl(null);
          setResumeInvoiceStatus(null);
          setResumePaymentStatus(null);
        }

        /**
         * Scheduled downgrade rule:
         * if likely downgrade and not restart, show "no charge today"
         * instead of credit/proration noise.
         */
        if (effectiveIsLikelyDowngrade && !restartFlow) {
          if (isStaleRequest()) {
            console.log(
              "[ReviewChangesDrawer2] Ignoring stale scheduled-downgrade " +
              "preview response:",
              {
                requestId,
                selectedPlan: stableSelectedPlan,
                seatCount: stableSeatCount,
              },
            );
            return;
          }

          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

          setRequiresRestart(false);
          setRestartReason(null);
          setPreview({
            basePrice: 0,
            additionalSeatsPrice: 0,
            prorationAdjustment: 0,
            total: 0,
            amountDueNow: 0,
            breakdownTotal: 0,
            subtotal: 0,
            invoiceTotal: 0,
            amountPaid: 0,
            amountRemaining: 0,
            appliedBalance: 0,
            scheduled: true,
          });

          console.log(
            "[ReviewChangesDrawer2] Scheduled downgrade preview path.",
            {
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
              currentSubscription: stableCurrentSubscription,
            },
          );

          return;
        }

        const data = await previewBillingChange({
          subscriptionId: currentSubscriptionId,
          subscriptionItemId: currentSubscriptionItemId,
          newPriceId: computedNewPriceId,
          seatCount: stableSeatCount,
        });

        if (isStaleRequest()) {
          console.log(
            "[ReviewChangesDrawer2] Ignoring stale billing preview response:",
            {
              requestId,
              data,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );
          return;
        }

        if (isPendingBillingChangePreviewUnavailable(data)) {
          console.warn(
            "[ReviewChangesDrawer2] Preview unavailable due to pending " +
            "billing change. Deferring to BillingContext reconciliation:",
            {
              data,
              pendingImmediateChange,
              pendingImmediateInvoiceId,
              pendingImmediateInvoiceDetails,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          if (
            pendingInvoiceRelevantToCurrentReview &&
            pendingImmediateInvoiceIsActionable
          ) {
            inFlightPreviewRequestKeyRef.current = null;
            lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

            setRequiresRestart(false);
            setRestartReason(null);
            setPreview(null);
            setResumePaymentMode(true);
            setResumeInvoiceId(pendingImmediateInvoiceId || null);
            setResumeInvoiceUrl(pendingImmediateInvoiceUrl || null);
            setResumeInvoiceStatus(
              pendingImmediateInvoiceStatus || null,
            );
            setResumePaymentStatus(
              pendingImmediatePaymentStatus || null,
            );

            console.log(
              "[ReviewChangesDrawer2] Resume-payment mode activated " +
              "from provider-owned pendingChangeState after " +
              "previewUnavailable response:",
              {
                data,
                pendingChangeState,
                pendingImmediateInvoiceClassification,
                pendingInvoiceRelevantToCurrentReview,
                selectedPlan: stableSelectedPlan,
                seatCount: stableSeatCount,
              },
            );

            return;
          }

          if (
            pendingInvoiceRelevantToCurrentReview &&
            (
              pendingImmediateInvoiceIsChecking ||
              pendingImmediateInvoiceIsTransitional
            )
          ) {
            console.log(
              "[ReviewChangesDrawer2] Pending billing change is still " +
              "stabilizing in BillingContext. Clearing preview state " +
              "and waiting for a stable actionable or stale result:",
              {
                data,
                pendingImmediateChange,
                pendingImmediateInvoiceDetails,
                pendingImmediateInvoiceClassification,
                pendingInvoiceRelevantToCurrentReview,
                selectedPlan: stableSelectedPlan,
                seatCount: stableSeatCount,
              },
            );

            inFlightPreviewRequestKeyRef.current = null;
            lastCompletedPreviewRequestKeyRef.current = null;

            setPreview(null);
            setResumePaymentMode(false);
            setResumeInvoiceId(null);
            setResumeInvoiceUrl(null);
            setResumeInvoiceStatus(null);
            setResumePaymentStatus(null);
            setLoading(false);

            return;
          }

          console.warn(
            "[ReviewChangesDrawer2] Pending billing change is not " +
            "actionable after BillingContext reconciliation. Clearing " +
            "preview state and waiting for fresh billing state:",
            {
              data,
              pendingImmediateChange,
              pendingImmediateInvoiceDetails,
              pendingImmediateInvoiceClassification,
              pendingInvoiceRelevantToCurrentReview,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
            },
          );

          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = null;

          setPreview(null);
          setResumePaymentMode(false);
          setResumeInvoiceId(null);
          setResumeInvoiceUrl(null);
          setResumeInvoiceStatus(null);
          setResumePaymentStatus(null);
          setLoading(false);

          return;
        }

        if (data?.requiresRestart === true) {
          console.warn(
            "[ReviewChangesDrawer2] Billing preview requires restart:",
            data,
          );

          inFlightPreviewRequestKeyRef.current = null;
          lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

          previewCacheRef.current.set(previewRequestKey, {
            preview: null,
            requiresRestart: true,
            restartReason:
              data?.reason || "SUBSCRIPTION_RESTART_REQUIRED",
            cachedAt: Date.now(),
          });

          setRequiresRestart(true);
          setRestartReason(
            data?.reason || "SUBSCRIPTION_RESTART_REQUIRED",
          );
          setPreview(null);
          return;
        }

        inFlightPreviewRequestKeyRef.current = null;
        lastCompletedPreviewRequestKeyRef.current = previewRequestKey;

        previewCacheRef.current.set(previewRequestKey, {
          preview: data,
          requiresRestart: false,
          restartReason: null,
          cachedAt: Date.now(),
        });

        setRequiresRestart(false);
        setRestartReason(null);
        setPreview(data);

        console.log("[ReviewChangesDrawer2] Preview loaded:", {
          data,
          selectedPlan: stableSelectedPlan,
          seatCount: stableSeatCount,
        });
      } catch (err) {
        if (isStaleRequest()) {
          console.log(
            "[ReviewChangesDrawer2] Ignoring stale preview error:",
            {
              requestId,
              selectedPlan: stableSelectedPlan,
              seatCount: stableSeatCount,
              error: err?.message || err,
            },
          );
          return;
        }

        inFlightPreviewRequestKeyRef.current = null;

        console.error("[ReviewChangesDrawer2] fetch preview failed:", err);
        setPreview(null);
      } finally {
        if (!isStaleRequest()) {
          inFlightPreviewRequestKeyRef.current = null;
          setLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      ignore = true;
      activePreviewRequestRef.current = 0;
      inFlightPreviewRequestKeyRef.current = null;
    };
  }, [
    open,
    stableSelectedPlan,
    stableSeatCount,
    selectedSelfServe,
    selectedKey,
    previewBillingChange,
    effectiveIsLikelyDowngrade,
    pendingChangeState,
    pendingChangeMatchesCurrentSelection,
    pendingInvoiceRelevantToCurrentReview,
    pendingImmediateInvoiceId,
    pendingImmediateChange,
    pendingImmediateNextSeatCount,
    pendingImmediateNextPriceId,
    pendingImmediateInvoiceDetails,
    pendingImmediateInvoiceClassification,
    pendingImmediateInvoiceIsActionable,
    pendingImmediateInvoiceIsChecking,
    pendingImmediateInvoiceIsTransitional,
    pendingImmediateInvoiceIsStale,
    pendingImmediateInvoiceIsError,
    pendingImmediateInvoiceUrl,
    pendingImmediatePaymentStatus,
    currentSubscriptionId,
    currentSubscriptionItemId,
    currentSubscriptionPriceId,
    currentSubscriptionStatus,
    cancelingPendingChange,
    previewRequestKey,
  ]);

  useEffect(() => {
    if (!open) {
      inFlightPreviewRequestKeyRef.current = null;
      lastCompletedPreviewRequestKeyRef.current = null;
      activePreviewRequestRef.current = 0;
    }
  }, [open]);

useEffect(() => {
  if (!open) {
    resetTransientUiState();
  }
}, [open, resetTransientUiState]);

  useEffect(() => {
  const handlePageShow = (event) => {
    if (!event.persisted) return;
    if (!open) return;

    console.log(
      "[ReviewChangesDrawer2] Page restored from bfcache while drawer " +
      "is open. Resetting transient drawer UI state.",
      {
        open,
        reviewContext,
      },
    );

    resetTransientUiState();
  };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [open, reviewContext, resetTransientUiState]);
  const handleCancelPendingBillingChange = async () => {
    if (cancelingPendingChange) return;

    if (!pendingImmediateChange && !resumePaymentMode) {
      console.warn(
        "[ReviewChangesDrawer2] Cannot cancel pending billing change " +
        "because there is no pending billing state in the drawer.",
        {
          pendingImmediateChange,
          pendingImmediateInvoiceId,
          currentSubscriptionId,
          resumePaymentMode,
        },
      );
      return;
    }

    setCancelingPendingChange(true);
    activePreviewRequestRef.current = 0;
    inFlightPreviewRequestKeyRef.current = null;
    lastCompletedPreviewRequestKeyRef.current = null;

    try {
      console.log(
        "[ReviewChangesDrawer2] Canceling pending billing change:",
        {
          pendingImmediateInvoiceId,
          currentSubscriptionId,
          pendingImmediateChange,
          selectedPlan: stableSelectedPlan,
          seatCount: stableSeatCount,
        },
      );

      const result = await cancelPendingBillingChange();

      console.log(
        "[ReviewChangesDrawer2] Pending billing change cancel result:",
        result,
      );

      if (result?.success) {
        setResumePaymentMode(false);
        setResumeInvoiceUrl(null);
        setResumeInvoiceId(null);
        setResumeInvoiceStatus(null);
        setResumePaymentStatus(null);
        setPreview(null);
        setRequiresRestart(false);
        setRestartReason(null);

        if (typeof onPendingBillingCancelSuccess === "function") {
          onPendingBillingCancelSuccess();
        } else {
          onClose();
        }

        return;
      }

      console.warn(
        "[ReviewChangesDrawer2] Pending billing change cancel did not " +
        "return success:",
        result,
      );
    } catch (err) {
      console.error(
        "[ReviewChangesDrawer2] Failed to cancel pending billing change:",
        err,
      );
    } finally {
      setCancelingPendingChange(false);
    }
  };

  /* ---------------------------------------------------------------
   * Early render bail-outs
   * ------------------------------------------------------------- */
  if (!open || !reviewContext || !stableSelectedPlan) {
    return null;
  }

  if (!selectedSelfServe) {
    return null;
  }

  if (currentPlanIsSalesLed) {
    console.warn(
      "[ReviewChangesDrawer2] blocked: current subscription is sales-led / non-self-serve",
      {
        currentPlanKey,
        currentSubscription: stableCurrentSubscription,
        selectedKey,
        selectedPlan: stableSelectedPlan,
      },
    );
    return null;
  }
  /* ---------------------------------------------------------------
   * Renewal/effective date helper
   * ------------------------------------------------------------- */
  const nextRenewalDate = (() => {
    if (currentSubscriptionStatus === "trialing") {
      const anchor = new Date();

      if (newInterval === "year") {
        anchor.setFullYear(anchor.getFullYear() + 1);
      } else {
        anchor.setMonth(anchor.getMonth() + 1);
      }

      return anchor;
    }

    const canceled = currentSubscriptionStatus === "canceled";
    const rd = stableCurrentSubscription?.renewalDate;
    const anchorValid = !!rd && !Number.isNaN(new Date(rd).getTime());

    if (effectiveIsLikelyDowngrade && anchorValid) {
      return new Date(stableCurrentSubscription.renewalDate);
    }

    if (!intervalSwitched && !canceled && anchorValid) {
      return new Date(stableCurrentSubscription.renewalDate);
    }

    const anchor = new Date();

    if (newInterval === "year") {
      anchor.setFullYear(anchor.getFullYear() + 1);
    } else {
      anchor.setMonth(anchor.getMonth() + 1);
    }

    return anchor;
  })();

  /* ---------------------------------------------------------------
   * Cost breakdown rows
   * ------------------------------------------------------------- */
  const rows = [];

  if (preview) {
    const scheduledPreview =
      Boolean(preview?.scheduled) || Boolean(effectiveIsLikelyDowngrade);

    const hasNumericPreviewValues =
      Number.isFinite(preview?.basePrice) &&
      Number.isFinite(preview?.additionalSeatsPrice) &&
      Number.isFinite(preview?.prorationAdjustment);

    if (!scheduledPreview && !hasNumericPreviewValues) {
      console.warn(
        "[ReviewChangesDrawer2] Skipping cost-row rendering because " +
        "preview does not contain numeric billing fields:",
        {
          preview,
          selectedPlan: stableSelectedPlan,
          seatCount: stableSeatCount,
        },
      );
    }

    if (!scheduledPreview && hasNumericPreviewValues) {
      if (intervalSwitched) {
        const fullCycleCharge =
          Number.isFinite(Number(preview?.additionalSeatsPrice)) ?
            Number(preview.additionalSeatsPrice) :
            (
              (stableSelectedPlan?.flatFee || 0) +
              (stableSelectedPlan?.perSeat || 0) * stableSeatCount
            );

        rows.push({
          label: newInterval === "year"
            ? "New annual plan total"
            : "New monthly plan total",
          value: fullCycleCharge,
        });

        console.log("[ReviewChangesDrawer2] Full-cycle charge:", {
          fullCycleCharge,
          intervalSwitched,
          preview,
        });
      }

      if (!intervalSwitched) {
        if (preview.basePrice !== 0) {
          rows.push({
            label: "Prorated fixed fee",
            value: preview.basePrice,
          });
        }

        rows.push({
          label: isSeatOnly
            ? "Prorated user adjustment"
            : "Prorated plan adjustment",
          value: preview.additionalSeatsPrice,
        });
      }

      if (preview.prorationAdjustment !== 0) {
        rows.push({
          label: "Prorated credit",
          value: preview.prorationAdjustment,
        });
      }

      console.log("[ReviewChangesDrawer2] Cost rows:", rows);
    }
  }

  const breakdown = rows;

  const breakdownTotal = rows.reduce(
    (sum, row) => sum + row.value,
    0,
  );

  const netProratedChange =
    Number.isFinite(Number(breakdownTotal)) ?
      Number(breakdownTotal) :
      0;

  const hasNetProratedChange =
    Math.abs(netProratedChange) >= 0.01;

  const amountDueNow =
    Number.isFinite(Number(preview?.amountDueNow)) ?
      Number(preview.amountDueNow) :
      breakdownTotal;

  const invoiceSubtotal =
    Number.isFinite(Number(preview?.subtotal)) ?
      Number(preview.subtotal) :
      null;

  const invoiceTotal =
    Number.isFinite(Number(preview?.invoiceTotal)) ?
      Number(preview.invoiceTotal) :
      breakdownTotal;

  const estimatedTax =
    Number.isFinite(invoiceTotal) &&
    Number.isFinite(invoiceSubtotal) ?
      Math.max(0, invoiceTotal - invoiceSubtotal) :
      null;

  const hasEstimatedTax =
    Number.isFinite(estimatedTax) &&
    estimatedTax >= 0.01;

  const dueTodayDelta =
    hasNetProratedChange ?
      Number(amountDueNow) - Number(netProratedChange) :
      0;

  const dueTodayCreditOrBalance =
    dueTodayDelta < 0 ?
      Math.abs(dueTodayDelta) :
      0;

  const hasDueTodayCreditOrBalance =
    Number.isFinite(dueTodayCreditOrBalance) &&
    dueTodayCreditOrBalance >= 0.01;

  const hasDueTodayOffset =
    hasNetProratedChange &&
    Math.abs(dueTodayDelta) >= 0.01;

  /* ---------------------------------------------------------------
   * Header summary labels
   * ------------------------------------------------------------- */
  const summaryHeading = !hasActiveSub
    ? "New subscription"
    : (effectiveIsLikelyDowngrade
      ? "Scheduled change"
      : (isSeatOnly ? "User change" : "Plan change"));

//  const primarySummaryLine = isSeatOnly
//    ? `${seatCount} ${Number(seatCount) === 1 ? "user" : "users"}`
//    : `${formatPlanChipLabel(currentSubscription?.planName)} to ${formatPlanChipLabel(selectedPlan?.name)}`;

  const selectedBillingCadence = stableSelectedPlan?.interval === "annual"
    ? "Billed annually"
    : stableSelectedPlan?.interval === "monthly"
      ? "Billed monthly"
      : (newInterval === "year" ? "Billed annually" : "Billed monthly");

  /* ---------------------------------------------------------------
   * Render
   * ------------------------------------------------------------- */
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      keepMounted={false}
      PaperProps={{
        sx: {
          width: drawerWidth,
          display: "flex",
          flexDirection: "column",
        },
      }}
      SlideProps={{
        direction: "left",
        mountOnEnter: true,
        unmountOnExit: true,
      }}
    >
      {/* ----------------------------------------------------------
       * Header
       * -------------------------------------------------------- */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 700,
            color: "#0F1A2A",
          }}
        >
          Review your changes
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            lineHeight: 1.55,
          }}
        >
          {resumePaymentMode ? (
            <>
              Your previous billing change is still awaiting payment.
              Resume payment to complete the update, or cancel to close this
              drawer and return to the subscription page.
            </>
          ) : effectiveIsLikelyDowngrade ? (
            <>
              Your changes will take effect on{" "}
              {nextRenewalDate.toLocaleDateString()}. You’ll keep your current
              plan and users until then.
            </>
          ) : isRestart ? (
            <>
              You’re restarting your subscription. You’ll be redirected to
              checkout to create a new subscription.
            </>
          ) : isTrialing ? (
            <>
              You’re currently in a free trial. If you continue, your trial
              will end and you’ll move to the selected plan.
            </>
          ) : hasFailedBilling ? (
            <>
              Your subscription needs billing attention. Review the details
              below and continue to update your plan and payment flow.
            </>
          ) : isSeatOnly ? (
            <>
              Review the user change below. You’ll be redirected to complete
              checkout if any amount is due today.
            </>
          ) : (
            <>
              Confirm the details below. You’ll be redirected to complete
              checkout if any amount is due today.
            </>
          )}
        </Typography>
      </Box>

      <Divider />

      {/* ----------------------------------------------------------
       * Missing payment method warning
       * -------------------------------------------------------- */}
      {showPaymentMethodNotice && (
        <Alert
          severity="info"
          sx={{ mx: 3, mt: 2 }}
        >
          There’s no valid payment method on file yet. You can still continue —
          Stripe will let you add or confirm payment details during the next
          payment step if needed.
        </Alert>
      )}

      {/* ----------------------------------------------------------
       * Main content
       * -------------------------------------------------------- */}
      <Box sx={{ p: 3, pt: 2, flexGrow: 1, overflowY: "auto" }}>
        {resumePaymentMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              A payment is already in progress for this billing change.
            </Typography>
            <Typography variant="body2">
              Resume the existing payment instead of creating a new one.
            </Typography>
            {resumeInvoiceId ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Invoice: {resumeInvoiceId}
                {resumeInvoiceStatus ?
                  ` • Invoice status: ${resumeInvoiceStatus}` :
                  ""}
                {resumePaymentStatus &&
                resumePaymentStatus !== resumeInvoiceStatus ?
                  ` • Payment status: ${resumePaymentStatus}` :
                  ""}
              </Typography>
            ) : null}
          </Alert>
        )}

        {isSubmissionBlockedByPendingInvoiceState && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              We’re still reconciling your previous billing attempt.
            </Typography>
            <Typography variant="body2">
              Please wait a moment before continuing. Once the invoice state is
              stable, this drawer will either let you resume payment or continue
              with a fresh preview.
            </Typography>
            {pendingImmediateInvoiceId ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Invoice: {pendingImmediateInvoiceId}
                {pendingImmediateInvoiceClassification ?
                  ` • State: ${pendingImmediateInvoiceClassification}` :
                  ""}
              </Typography>
            ) : null}
          </Alert>
        )}

        {/* What is changing */}
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 700,
            color: "#0F1A2A",
          }}
        >
          {summaryHeading}
        </Typography>

        {/* Summary chips / text */}
        <Typography
          variant="body1"
          gutterBottom
          component="div"
          sx={{
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
          }}
        >
          {hasActiveSub && !isSeatOnly ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: "grey.200",
                  fontWeight: 500,
                }}
              >
                {formatPlanChipLabel(stableCurrentSubscription?.planName)}
              </Box>

              <ArrowForwardIosIcon
                fontSize="inherit"
                sx={{ verticalAlign: "middle" }}
              />

              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                  fontWeight: 600,
                }}
              >
                {formatPlanChipLabel(stableSelectedPlan?.name)}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                  fontWeight: 600,
                }}
              >
                {stableSeatCount} {Number(stableSeatCount) === 1 ?
                  "user" : "users"}
              </Box>
            </Box>
          )}
        </Typography>

        {/* Selected plan pricing reminder */}
        {!stableSelectedPlan?.pricingDisplay?.includes?.("contact") &&
          selectedPlanTotal !== null && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                  color: "#475569",
                  lineHeight: 1.5,
                }}
              >
                {formatPlanChipLabel(stableSelectedPlan?.name)} total:{" "}
                <strong>{formatCurrencyNoCents(selectedPlanTotal)}</strong>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  fontFamily:
                    "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                }}
              >
                {selectedBillingCadence}
              </Typography>
            </Box>
          )}

        {/* Cost breakdown */}
        <Typography
          variant="subtitle2"
          sx={{
            mt: 3,
            mb: 1,
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
            fontWeight: 700,
            color: "#0F1A2A",
          }}
        >
          {effectiveIsLikelyDowngrade ? "Billing summary" : "Charges due now"}
        </Typography>

        {loading && (
          <>
            <Skeleton height={28} sx={{ mb: 1 }} />
            <Skeleton height={28} sx={{ mb: 1 }} />
            <Skeleton height={32} width="60%" />
          </>
        )}

        {!loading && requiresRestart && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This subscription is canceled in Stripe, so we can’t preview
              prorations. Select a plan and proceed to restart.
              {restartReason ? ` (${restartReason})` : ""}
            </Alert>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              You can still proceed — checkout will create a new subscription.
            </Typography>
          </>
        )}

        {!loading && !resumePaymentMode && !requiresRestart && preview && (
          <>
            {effectiveIsLikelyDowngrade ? (
              <>
                <List dense disablePadding>
                  <ListItem disableGutters sx={{ py: 0.5, px: 0 }}>
                    <ListItemText primary="Total excluding tax" />
                    <Typography fontWeight={600}>
                      {formatCurrency(0)}
                    </Typography>
                  </ListItem>
                </List>

                <Divider sx={{ my: 1 }} />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  No charge today. Your updated plan and users will start on{" "}
                  {nextRenewalDate.toLocaleDateString()}. Any billing
                  adjustments are handled automatically at renewal.
                </Typography>
              </>
            ) : (
              <>
                <List dense disablePadding>
                  {breakdown.map((row) => (
                    <ListItem
                      key={row.label}
                      disableGutters
                      sx={{ py: 0.5, px: 0 }}
                    >
                      <ListItemText primary={row.label} />
                      <Typography>{formatCurrency(row.value)}</Typography>
                    </ListItem>
                  ))}

                  {hasNetProratedChange && (
                    <ListItem disableGutters sx={{ py: 0.5, px: 0 }}>
                      <ListItemText primary="Net prorated change" />
                      <Typography fontWeight={600}>
                        {formatCurrency(netProratedChange)}
                      </Typography>
                    </ListItem>
                  )}

                  {hasDueTodayCreditOrBalance && (
                    <ListItem disableGutters sx={{ py: 0.5, px: 0 }}>
                      <ListItemText primary="Credits/balance applied today" />
                      <Typography>
                        {formatCurrency(-dueTodayCreditOrBalance)}
                      </Typography>
                    </ListItem>
                  )}
                </List>

                <Divider sx={{ my: 1 }} />

                <ListItem disableGutters sx={{ py: 0.5, px: 0 }}>
                  <ListItemText primary="Tax" />
                  {hasEstimatedTax ? (
                    <Typography>{formatCurrency(estimatedTax)}</Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Calculated at checkout
                    </Typography>
                  )}
                </ListItem>

                <ListItem disableGutters sx={{ py: 0.5, px: 0 }}>
                  <ListItemText primary="Amount due now" />
                  <Typography fontWeight={600}>
                    {formatCurrency(amountDueNow)}
                  </Typography>
                </ListItem>

                {hasDueTodayOffset && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    This is still a preview. The proration lines above show the
                    subscription-change lines only. Stripe will confirm any
                    invoice-level credits, customer balance, tax, and the final
                    amount due during checkout.
                  </Typography>
                )}
              </>
            )}

            {/* Next-cycle reminder */}
            {preview && stableSelectedPlan && (
              <>
                <Divider sx={{ my: 1 }} />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontFamily:
                      "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                    lineHeight: 1.55,
                  }}
                >
                  {effectiveIsLikelyDowngrade ? (
                    <>
                      Starting {nextRenewalDate.toLocaleDateString()}, you’ll be
                      on <strong>{stableSelectedPlan.name}</strong> at{" "}
                      {formatCurrency(
                        (stableSelectedPlan.flatFee || 0) +
                        (stableSelectedPlan.perSeat || 0) * stableSeatCount,
                      )}
                      .
                    </>
                  ) : isTrialing ? (
                    <>
                      Your free trial will end when you complete checkout.
                      You’ll start on the <strong>{stableSelectedPlan.name}</strong>{" "}
                      plan at{" "}
                      {formatCurrency(
                        (stableSelectedPlan.flatFee || 0) +
                        (stableSelectedPlan.perSeat || 0) * stableSeatCount,
                      )}, and your next renewal will be on{" "}
                      {nextRenewalDate.toLocaleDateString()}.
                    </>
                  ) : hasFailedBilling ? (
                    <>
                      Once your billing update is completed, the{" "}
                      <strong>{stableSelectedPlan.name}</strong> plan will bill at{" "}
                      {formatCurrency(
                        (stableSelectedPlan.flatFee || 0) +
                        (stableSelectedPlan.perSeat || 0) * stableSeatCount,
                      )}.
                    </>
                  ) : (
                    <>
                      When your subscription renews on{" "}
                      {nextRenewalDate.toLocaleDateString()}, the{" "}
                      <strong>{stableSelectedPlan.name}</strong> plan will bill at{" "}
                      {formatCurrency(
                        (stableSelectedPlan.flatFee || 0) +
                        (stableSelectedPlan.perSeat || 0) * stableSeatCount,
                      )}
                      .
                    </>
                  )}
                </Typography>
              </>
            )}

            {!effectiveIsLikelyDowngrade &&
              !hasDueTodayOffset &&
              preview.prorationAdjustment < 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Credits from unused time on your current subscription are
                  already reflected above. Final tax and invoice settlement
                  are confirmed by Stripe during checkout.
                </Typography>
              )}
          </>
        )}
      </Box>

      {/* ----------------------------------------------------------
       * Sticky footer actions
       * -------------------------------------------------------- */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Tooltip
          title={
            isSubmissionBlockedByPendingInvoiceState ?
              "We’re still reconciling your previous billing attempt" :
              undefined
          }
          arrow
          disableHoverListener={
            !isSubmissionBlockedByPendingInvoiceState
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={
                submitting
                  ? <CircularProgress size={20} />
                  : <CheckCircleOutlineIcon />
              }
              disabled={
                loading ||
                submitting ||
                cancelingPendingChange ||
                isSubmissionBlockedByPendingInvoiceState
              }
              onClick={async () => {
                if (submitting) return;

                if (isSubmissionBlockedByPendingInvoiceState) {
                  console.warn(
                    "[ReviewChangesDrawer2] Blocking checkout submit " +
                    "because pending invoice state is still unstable:",
                    {
                      pendingImmediateChange,
                      pendingImmediateInvoiceId,
                      pendingImmediateInvoiceClassification,
                      pendingInvoiceRelevantToCurrentReview,
                      selectedPlan: stableSelectedPlan,
                      seatCount: stableSeatCount,
                    },
                  );
                  return;
                }

                /* ----------------------------------------------------
                 * Enterprise-grade Stripe navigation strategy
                 *
                 * Goal:
                 * - true Checkout → same tab
                 * - hosted invoice edge/recovery flows → separate tab only
                 *   when intentionally chosen
                 *
                 * Why:
                 * - Checkout has reliable success/cancel return URLs back
                 *   into the app, so same-tab is the cleanest SaaS UX.
                 * - Hosted invoice pages are less reliable as an app-return
                 *   surface, so we reserve them for edge flows like
                 *   resume-payment.
                 * - This prevents the mixed ownership / blank-tab behavior
                 *   caused by opening new tabs too early for ordinary
                 *   billing changes.
                 * -------------------------------------------------- */
                let paymentWindow = null;

                setSubmitting(true);

                try {
                  if (resumePaymentMode) {
                    if (resumeInvoiceUrl) {
                      paymentWindow = openBlankPaymentWindow();

                      const launchSnapshot = buildStripeLaunchSnapshot();

                      console.log(
                        "[ReviewChangesDrawer2] Persisting Stripe " +
                        "handoff state for resume-payment redirect:",
                        {
                          launchSnapshot,
                          resumeInvoiceId,
                          resumeInvoiceStatus,
                          selectedPlan: stableSelectedPlan,
                          seatCount: stableSeatCount,
                        },
                      );

                      if (typeof onStripeHandoffStart === "function") {
                        onStripeHandoffStart({
                          launchSnapshot,
                        });
                      }

                      setSubmitting(false);
                      navigatePaymentWindowOrFallback(
                        paymentWindow,
                        resumeInvoiceUrl,
                      );
                      return;
                    }

                    console.warn(
                      "[ReviewChangesDrawer2] resume payment requested but no invoice URL is available.",
                      {
                        resumeInvoiceId,
                        resumeInvoiceStatus,
                        selectedPlan: stableSelectedPlan,
                        seatCount: stableSeatCount,
                      },
                    );

                    closePaymentWindowIfUnused(paymentWindow);
                    setSubmitting(false);
                    return;
                  }

                  const selectedPlanKey =
                    (
                      stableSelectedPlan?.key ||
                      stableSelectedPlan?.name ||
                      ""
                    )
                      .toString()
                      .toLowerCase();

                  const cfg = planConfigs[selectedPlanKey] || {};
                  const isSelectedSelfServe =
                    (cfg?.availability?.selfServe !== false) &&
                    (cfg?.cta?.type !== "contact_sales") &&
                    (cfg?.pricingDisplay !== "contact");

                  if (!isSelectedSelfServe) {
                    console.warn(
                      "[ReviewChangesDrawer2] blocked checkout: non-self-serve plan",
                      {
                        selectedPlanKey,
                        selectedPlan: stableSelectedPlan,
                      },
                    );
                    closePaymentWindowIfUnused(paymentWindow);
                    setSubmitting(false);
                    onClose();
                    return;
                  }

                  const restartFlow =
                    currentSubscriptionStatus === "canceled" ||
                    currentSubscriptionStatus === "incomplete_expired";

                  /* ------------------------------------------------
                   * Only pre-open a payment tab for flows where we
                   * already KNOW we want a separate hosted-invoice tab.
                   *
                   * Why:
                   * - True Stripe Checkout should use same-tab navigation
                   *   so success/cancel return flows come back cleanly to
                   *   the app.
                   * - Hosted invoice pages are reserved for edge/recovery
                   *   flows, especially resume-payment.
                   * - Pre-opening a blank tab for ordinary upgrade flows
                   *   creates noisy UX and can produce the exact blank-tab
                   *   behavior we are trying to eliminate.
                   *
                   * Enterprise rule:
                   * - resume hosted invoice → new tab
                   * - true Checkout         → same tab
                   * - no external page      → no pre-opened tab
                   * ------------------------------------------------ */
                  if (resumePaymentMode) {
                    paymentWindow = openBlankPaymentWindow();
                  }

                  const resp = await switchIntervalAndPay({
                    ...(restartFlow
                      ? { newPriceId: stableSelectedPlan.priceId }
                      : (!isSeatOnly ?
                        { newPriceId: stableSelectedPlan.priceId } :
                        {})),
                    seatCount: stableSeatCount,
                  });

                  if (resp?.scheduled && resp?.effectiveAt) {
                    console.log(
                      "[ReviewChangesDrawer2] scheduled change:",
                      resp,
                    );
                    closePaymentWindowIfUnused(paymentWindow);
                    setSubmitting(false);
                    onClose();
                    return;
                  }

                  if (resp?.needsPayment && resp?.url) {
                    const launchSnapshot = buildStripeLaunchSnapshot();
                    const paymentPageType =
                      resolveStripePaymentPageType(resp);

                    console.log(
                      "[ReviewChangesDrawer2] Persisting Stripe " +
                      "handoff state for payment redirect:",
                      {
                        launchSnapshot,
                        response: resp,
                        paymentPageType,
                        selectedPlan: stableSelectedPlan,
                        seatCount: stableSeatCount,
                      },
                    );

                    if (typeof onStripeHandoffStart === "function") {
                      onStripeHandoffStart({
                        launchSnapshot,
                      });
                    }

                    setSubmitting(false);

                    /* ----------------------------------------------
                     * Enterprise navigation policy
                     *
                     * checkout:
                     * - same-tab
                     * - reliable app return through success/cancel URLs
                     *
                     * hosted_invoice:
                     * - edge/recovery/manual-pay flow
                     * - prefer separate tab when we intentionally opened one
                     *
                     * fallback:
                     * - if backend unexpectedly returns a hosted invoice in a
                     *   non-resume flow, fall back to same-tab instead of
                     *   leaving the user with a broken blank tab experience
                     * -------------------------------------------- */
                    if (paymentPageType === "checkout") {
                      closePaymentWindowIfUnused(paymentWindow);
                      window.location.href = resp.url;
                      return;
                    }

                    if (paymentPageType === "hosted_invoice") {
                      if (paymentWindow && !paymentWindow.closed) {
                        navigatePaymentWindowOrFallback(
                          paymentWindow,
                          resp.url,
                        );
                        return;
                      }

                      console.warn(
                        "[ReviewChangesDrawer2] Hosted invoice returned " +
                        "without a pre-opened payment tab. Falling back " +
                        "to same-tab navigation for safety.",
                        {
                          response: resp,
                          selectedPlan: stableSelectedPlan,
                          seatCount: stableSeatCount,
                        },
                      );

                      window.location.href = resp.url;
                      return;
                    }

                    closePaymentWindowIfUnused(paymentWindow);
                    window.location.href = resp.url;
                    return;
                  }

                  if (resp?.needsPayment === false) {
                    closePaymentWindowIfUnused(paymentWindow);

                    console.log(
                      "[ReviewChangesDrawer2] Billing change completed " +
                      "without external payment handoff:",
                      {
                        response: resp,
                        selectedPlan: stableSelectedPlan,
                        seatCount: stableSeatCount,
                      },
                    );

                    setSubmitting(false);
                    onClose();

                    if (resp?.invoiceId) {
                      window.location.href =
                        `${window.location.origin}` +
                        `/billing/checkout-result?invoice_id=${resp.invoiceId}`;
                    }

                    return;
                  }

                  /**
                   * ReviewChangesDrawer2 is the active owner of the confirm
                   * runtime path for SubscriptionPage2.
                   *
                   * At this point:
                   * - scheduled changes have already returned early
                   * - payment-required flows have already redirected
                   * - no-payment-due flows have already closed cleanly
                   *
                   * If execution reaches here without a redirect, simply
                   * release the submitting state.
                   */
                  closePaymentWindowIfUnused(paymentWindow);
                  setSubmitting(false);
                } catch (err) {
                  closePaymentWindowIfUnused(paymentWindow);
                  console.error("[ReviewChangesDrawer2] checkout failed", err);
                  setSubmitting(false);
                }
              }}
              sx={{ mb: 1 }}
            >
              {cancelingPendingChange ? (
                "Canceling pending change…"
              ) : isSubmissionBlockedByPendingInvoiceState ? (
                "Reconciling previous payment…"
              ) : submitting ? (
                effectiveIsLikelyDowngrade ? "Scheduling…" : "Redirecting…"
              ) : resumePaymentMode ? (
                "Resume payment"
              ) : (
                effectiveIsLikelyDowngrade ?
                  "Schedule downgrade" :
                  "Proceed to Checkout"
              )}
            </Button>
          </span>
        </Tooltip>

        {resumePaymentMode ? (
          <Button
            fullWidth
            onClick={handleCancelPendingBillingChange}
            disabled={submitting || cancelingPendingChange}
          >
            {cancelingPendingChange ?
              "Canceling pending change…" :
              "Cancel pending change"}
          </Button>
        ) : (
          <Button
            fullWidth
            onClick={onClose}
            disabled={submitting || cancelingPendingChange}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

ReviewChangesDrawer2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPendingBillingCancelSuccess: PropTypes.func,
  reviewContext: PropTypes.shape({
    selectedPlan: PropTypes.object,
    seatCount: PropTypes.number,
    currentSubscription: PropTypes.object,
    openedAt: PropTypes.number,
  }),
  onStripeHandoffStart: PropTypes.func,
};

export default ReviewChangesDrawer2;